import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../utils/AuthContext';
import { getBookingByAwb, updateBooking, listBookingsForRange } from '../utils/db/bookings';
import { getProfile } from '../utils/db/profiles';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { Hash, Save, Edit } from 'lucide-react';

import { findSector, calculateRate, getRateConfiguration } from '../utils/db/rates';

const BookingModify = () => {
  const { user } = useAuth();
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm();
  const [bookingId, setBookingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [filters, setFilters] = useState({ customerId: '', fromDate: '', toDate: '' });
  const [selectedFilterCustomer, setSelectedFilterCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [modes, setModes] = useState([]);
  const [products, setProducts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);

  React.useEffect(() => {
    if (user) {
      loadProfileParams();
    }
  }, [user]);

  const loadProfileParams = async () => {
    try {
      const profile = await getProfile(user.id);
      if (profile) {
        if (profile.modes) setModes(profile.modes);
        if (profile.products) setProducts(profile.products);
        if (profile.sectors) setSectors(profile.sectors);
      }
    } catch (e) {
      console.error('Failed to load profile params', e);
    }
  };

  // Rule based calculation
  const watchedWeight = watch('weight');
  const watchedPincode = watch('pincode');
  const watchedMode = watch('mode');
  const watchedType = watch('type'); // Product Name
  const watchedCustomerId = watch('customerId'); // Customer ID
  // Note: customerIdUuid is the hidden field holding the system UUID for the customer, but BookingModify sets customerId directly to the displayed string?
  // Let's check handleAwbBlur: setValue('customerId', booking.customers?.customer_id || booking.customer_id);
  // Wait, in BookingModify, the 'customerId' field is DISPLAY only (customer_id string). The actual DB UUID is in 'customerIdUuid'.
  // Rate config requires the UUID.
  const watchedCustomerIdUuid = watch('customerIdUuid');

  React.useEffect(() => {
    const calculate = async () => {
      // We need the system UUID for the customer to find rates, not the display string
      if (!watchedWeight || !watchedPincode || !watchedMode || !watchedType || !watchedCustomerIdUuid) return;

      // Find Product ID from Name
      const product = products.find(p => p.name === watchedType);
      if (!product) return;
      const productId = product.id;

      // Find Sector
      const sectorId = findSector(watchedPincode, sectors);

      if (!sectorId) {
        // console.warn('Sector not found for pincode:', watchedPincode);
        return;
      }

      setIsCalculating(true);
      try {
        // Fetch rate for this SPECIFIC customer (using UUID)
        const rateConfig = await getRateConfiguration(user.id, productId, watchedMode, watchedCustomerIdUuid);

        if (rateConfig) {
          const amount = calculateRate(rateConfig, rateConfig.rates, sectorId, watchedWeight);

          if (amount !== null) {
            setValue('amount', amount);
          }
        }
      } catch (e) {
        console.error("Auto calculation error", e);
      } finally {
        setIsCalculating(false);
      }
    };

    const timer = setTimeout(calculate, 500); // Debounce
    return () => clearTimeout(timer);
  }, [watchedWeight, watchedPincode, watchedMode, watchedType, watchedCustomerIdUuid, user.id, products, sectors, setValue]);
  const handleAwbBlur = async (e) => {
    const awb = e.target.value?.trim();
    if (!awb || !user) return;
    try {
      const booking = await getBookingByAwb(user.id, awb);
      if (!booking) {
        toast.error('No booking found for this AWB');
        return;
      }
      setBookingId(booking.id);
      // Populate all fields from the existing booking
      // Preserve UUID in hidden field, show human-readable customer_id in visible field if available
      setValue('customerIdUuid', booking.customer_id);
      setValue('customerId', booking.customers?.customer_id || booking.customer_id);
      setValue('date', booking.date);
      setValue('receiverName', booking.receiver_name);
      setValue('receiverAddress', booking.receiver_address || '');
      setValue('pincode', booking.pincode || '');
      setValue('mode', booking.mode);
      setValue('type', booking.type || '');
      setValue('weight', booking.weight);
      setValue('quantity', booking.quantity);
      setValue('amount', booking.amount);
      setValue('otherCharge', booking.other_charge || 0);
      toast.success('Booking loaded');
    } catch (err) {
      toast.error('Failed to fetch booking');
    }
  };

  const onSubmit = async (data) => {
    if (!bookingId) {
      toast.error('Load a booking by AWB first');
      return;
    }
    setIsLoading(true);
    try {
      await updateBooking(user.id, bookingId, data);
      toast.success('Booking updated successfully');
      // After successful update, clear form and increment AWB by 1
      const incrementAwb = (value) => {
        if (!value) return '';
        const m = String(value).match(/^(.*?)(\d+)$/);
        if (m) {
          const prefix = m[1];
          const digits = m[2];
          const next = (parseInt(digits, 10) + 1).toString().padStart(digits.length, '0');
          return `${prefix}${next}`;
        }
        return `${value}1`;
      };
      const nextAwb = incrementAwb(data.awbNo);
      // Clear loaded state so AWB becomes editable again
      setBookingId(null);
      // Reset form fields
      reset({
        awbNo: nextAwb,
        customerId: '',
        customerIdUuid: '',
        receiverName: '',
        receiverAddress: '',
        pincode: '',
        date: '',
        mode: '',
        type: '',
        weight: '',
        quantity: '',
        amount: '',
        otherCharge: ''
      });
    } catch (e) {
      toast.error('Failed to update booking');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchList = async () => {
    if (!user) return;
    setListLoading(true);
    try {
      if (!filters.customerId || !filters.fromDate || !filters.toDate) {
        setRows([]);
        setListLoading(false);
        return;
      }
      const list = await listBookingsForRange(
        user.id,
        filters.customerId,
        filters.fromDate,
        filters.toDate
      );
      setRows(list);
    } catch (e) {
      toast.error('Failed to fetch bookings');
    } finally {
      setListLoading(false);
    }
  };

  // Clear results if any filter becomes empty
  React.useEffect(() => {
    if (!filters.customerId || !filters.fromDate || !filters.toDate) {
      setRows([]);
    }
  }, [filters.customerId, filters.fromDate, filters.toDate]);

  return (
    <div className="space-y-6">
      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900">Modify Booking</h1>
        <p className="text-gray-600">Enter AWB number, press Tab to load data, then edit and save.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Consi no*</label>
              <div className="flex items-center">
                <Hash className="h-4 w-4 text-gray-400 mr-2" />
                <input
                  type="text"
                  {...register('awbNo', {
                    required: 'AWB number is required',
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: 'AWB must contain only letters and numbers'
                    },
                    maxLength: {
                      value: 20,
                      message: 'AWB number must be 20 characters or less'
                    }
                  })}
                  maxLength={20}
                  onBlur={handleAwbBlur}
                  className={`w-full px-2 py-2 border border-gray-300 rounded-md font-mono ${bookingId ? 'bg-gray-100 text-gray-700' : ''}`}
                  placeholder="AWB"
                  readOnly={!!bookingId}
                  aria-readonly={!!bookingId}
                  title={bookingId ? 'AWB cannot be changed on modify page' : 'Enter AWB and press Tab to load'}
                />
              </div>
              {errors.awbNo && <p className="mt-1 text-xs text-red-600">{errors.awbNo.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cust Id*</label>
              <input
                type="text"
                {...register('customerId', { required: 'Customer is required' })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                placeholder="Customer ID"
                readOnly
                aria-readonly="true"
                title="Customer ID cannot be changed on modify page"
              />
              {/* Hidden field to keep UUID for saving */}
              <input type="hidden" {...register('customerIdUuid')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Receiver*</label>
              <input type="text" {...register('receiverName', { required: 'Receiver name is required', maxLength: { value: 100, message: 'Receiver name must be 100 characters or less' } })} maxLength={100} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Receiver name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Address*</label>
              <input type="text" {...register('receiverAddress', { required: 'Receiver address is required', maxLength: { value: 200, message: 'Receiver address must be 200 characters or less' } })} maxLength={200} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Address" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Book date*</label>
              <input
                type="date"
                {...register('date', { required: 'Date is required' })}
                onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                className="w-full px-2 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pincode*</label>
              <input type="text" {...register('pincode', { required: 'Pincode is required', pattern: { value: /^[0-9]{6}$/, message: 'Please enter a valid 6-digit pincode' } })} maxLength={6} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Pincode" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mode*</label>
              <select
                {...register('mode', { required: 'Mode is required', validate: (v) => (v && v.length <= 20) || 'Mode must be 20 characters or less' })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select Mode</option>
                {modes.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {errors.mode && <p className="mt-1 text-xs text-red-600">{errors.mode.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">char Weight*</label>
              <input type="number" step="0.01" {...register('weight', { required: 'Weight is required' })} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Weight" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Qty*</label>
              <input type="number" {...register('quantity', { required: 'Quantity is required' })} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Qty" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type*</label>
              <select
                {...register('type', { required: 'Type is required', validate: (v) => (v && v.length <= 20) || 'Type must be 20 characters or less' })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select Type</option>
                {products.map(p => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
              {errors.type && <p className="mt-1 text-xs text-red-600">{errors.type.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Amount*</label>
              <input type="number" step="0.01" {...register('amount', { required: 'Amount is required' })} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Amount" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Other Charges</label>
              <input type="number" step="0.01" {...register('otherCharge')} className="w-full px-2 py-2 border border-gray-300 rounded-md" placeholder="Other" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={isLoading} className="px-5 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
              {isLoading ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
      {/* View Bookings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">View Bookings</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <CustomerAutocomplete
                label="Customer ID"
                placeholder="Type to search by Customer ID"
                onChange={(c) => {
                  setSelectedFilterCustomer(c);
                  setFilters({ ...filters, customerId: c.id });
                }}
                value={selectedFilterCustomer?.id}
                valueLabel={selectedFilterCustomer ? `${selectedFilterCustomer.customer_id} - ${selectedFilterCustomer.customer_name}` : ''}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchList}
                disabled={listLoading || !filters.customerId || !filters.fromDate || !filters.toDate}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 w-full"
              >
                {listLoading ? 'Loading...' : 'Show'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AWB</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.awb_no}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.customer_name || r.customer_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.receiver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{parseFloat(r.amount || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <button
                        type="button"
                        className="inline-flex items-center text-primary-600 hover:text-primary-800"
                        title="Edit this booking"
                        onClick={() => {
                          setValue('awbNo', r.awb_no);
                          handleAwbBlur({ target: { value: r.awb_no } });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BookingModify;


